import { ItemModel, ItemModelResponse, ApiResponse } from "../models/ItemModelTypes";

export const UNAUTHORIZED_EVENT = "unauthorized_access";
const getAuthToken = () => localStorage.getItem("authToken") || "";
const API_URL = process.env.REACT_APP_API_URL || "https://api-agamhive.agamvanigam.com";

interface FetchParams {
  page?: number;
  limit?: number;
  search?: string;
  filter?: Record<string, string | number | boolean>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}
const handleUnauthorized = () => {
    console.error("Unauthorized access. Dispatching event for navigation...");
    localStorage.removeItem("authToken");
    
    const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
    window.dispatchEvent(unauthorizedEvent);
    return { error: "Unauthorized. Please log in." };
};

export const fetchItemModelData = async (params: FetchParams = {}): Promise<ApiResponse<ItemModelResponse>> => {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();
  
  try {
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      limit: (params.limit || 10).toString(),
      ...(params.search ? { search: params.search } : {}),
      ...(params.filter
        ? Object.fromEntries(Object.entries(params.filter).map(([key, value]) => [key, String(value)]))
        : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    });
    
    const response = await fetch(`${API_URL}/itemmodels?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (response.status === 401) {
      return handleUnauthorized();
  }
    const jsonResponse = await response.json();
    if (!response.ok) {
      return { error: jsonResponse.msg || "Failed to fetch item models" };
    }
    
    return {
      data: {
        items: jsonResponse.data.data || [],
        total: jsonResponse.data.pagination.total || 0,
        page: jsonResponse.data.pagination.page || 1,
        limit: jsonResponse.data.pagination.limit || 10,
        totalPages: jsonResponse.data.pagination.totalPages || 1,
      }
    };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
};

export const createItemModel = async (itemModel: Omit<ItemModel, 'status'>): Promise<ApiResponse<ItemModel>> => {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const response = await fetch(`${API_URL}/itemmodels`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(itemModel),
    });

    if (response.status === 401) {
      return handleUnauthorized();
  }

    const data = await response.json();
    return response.ok ? { data } : { error: data?.msg || "Failed to create item model." };
  } catch (error) {
    return { error: "Network error. Please check your connection and try again." };
  }
};

export async function updateItemModel(id: string, itemModel: ItemModel): Promise<ApiResponse<ItemModel>> {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();


  const requestBody = {
    code: itemModel.code,
    name: itemModel.name,
    status: itemModel.status,
    updatedAt: new Date().toISOString(),
  };


  try {
    const response = await fetch(`${API_URL}/itemmodels/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();


    if (response.status === 401) {
      return handleUnauthorized();
  }
  

    return { data };
  } catch (error) {
    
    return { error: "Network error. Please try again." };
  }
}


export async function deleteItemModel(id: string): Promise<ApiResponse<null>> {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const response = await fetch(`${API_URL}/itemmodels/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401) {
      return handleUnauthorized();
  }

    return response.ok ? { data: null } : { error: "Failed to delete itemModel." };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
}
